import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSeller } from '../../context/SellerContext';
import InputField from '../InputField';
import { validatePassword } from '../../utils/validation';
import { sellerSettingsAPI, handleAPIError } from '../../utils/sellerAPI';

const SellerSettings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { seller, logout } = useSeller();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'password');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Password state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // Notifications state
  const [notifications, setNotifications] = useState({
    whatsapp: true,
    email: true,
    sms: false,
  });

  const handleLogout = () => {
    logout();
    navigate('/seller/login');
  };

  // Password handlers
  const handlePasswordChange = (field, value) => {
    setPasswords((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPasswordErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    const errors = {};

    if (!passwords.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    const newPasswordError = validatePassword(passwords.newPassword);
    if (newPasswordError) {
      errors.newPassword = newPasswordError;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setLoading(true);
    try {
      await sellerSettingsAPI.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });

      setMessage('Password changed successfully!');
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      const apiError = handleAPIError(err);
      setPasswordErrors({ submit: apiError.message });
    } finally {
      setLoading(false);
    }
  };

  // Notifications handlers
  const handleNotificationChange = (key) => {
    setNotifications((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      await sellerSettingsAPI.updateNotifications(notifications);
      setMessage('Notification preferences updated!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      const apiError = handleAPIError(err);
      setMessage(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      {/* Header Removed - Managed by Sidebar/Layout */}
      <div className="mb-6 px-4 pt-8">
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <nav className="space-y-2 p-4">
                <button
                  onClick={() => setActiveTab('password')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition
                    ${activeTab === 'password'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  Change Password
                </button>

                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition
                    ${activeTab === 'notifications'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  Notifications
                </button>

                <button
                  onClick={() => setActiveTab('bank')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition
                    ${activeTab === 'bank'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  Bank Details
                </button>

                <button
                  onClick={() => setActiveTab('tax')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition
                    ${activeTab === 'tax'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  Tax Details
                </button>

                <button
                  onClick={() => setActiveTab('signature')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition
                    ${activeTab === 'signature'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  E-Signature
                </button>

                <button
                  onClick={() => setActiveTab('legal')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition
                    ${activeTab === 'legal'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  Legal & Policies
                </button>
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow p-8">
              {/* Success Message */}
              {message && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-700 font-medium">{message}</p>
                </div>
              )}

              {/* Change Password Tab */}
              {activeTab === 'password' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    Change Password
                  </h2>

                  <form onSubmit={handleChangePassword} className="space-y-6 max-w-md">
                    <InputField
                      label="Current Password"
                      type="password"
                      name="currentPassword"
                      value={passwords.currentPassword}
                      onChange={(e) =>
                        handlePasswordChange('currentPassword', e.target.value)
                      }
                      placeholder="Enter current password"
                      error={passwordErrors.currentPassword}
                      required
                    />

                    <div>
                      <InputField
                        label="New Password"
                        type="password"
                        name="newPassword"
                        value={passwords.newPassword}
                        onChange={(e) =>
                          handlePasswordChange('newPassword', e.target.value)
                        }
                        placeholder="Enter new password"
                        error={passwordErrors.newPassword}
                        required
                      />

                      <div className="mt-4 space-y-2 text-sm">
                        <p className="font-medium text-gray-700">Password must contain:</p>
                        <div className="space-y-1 text-gray-600">
                          <p>
                            {passwords.newPassword.length >= 8
                              ? '✓'
                              : '○'}{' '}
                            Minimum 8 characters
                          </p>
                          <p>
                            {/[A-Z]/.test(passwords.newPassword)
                              ? '✓'
                              : '○'}{' '}
                            1 Capital letter (A-Z)
                          </p>
                          <p>
                            {/[0-9]/.test(passwords.newPassword)
                              ? '✓'
                              : '○'}{' '}
                            1 Number
                          </p>
                          <p>
                            {/[@$!%*?&]/.test(passwords.newPassword)
                              ? '✓'
                              : '○'}{' '}
                            1 Special Character (@$!%*?&)
                          </p>
                        </div>
                      </div>
                    </div>

                    <InputField
                      label="Confirm Password"
                      type="password"
                      name="confirmPassword"
                      value={passwords.confirmPassword}
                      onChange={(e) =>
                        handlePasswordChange('confirmPassword', e.target.value)
                      }
                      placeholder="Re-enter new password"
                      error={passwordErrors.confirmPassword}
                      required
                    />

                    {passwordErrors.submit && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-600 text-sm">{passwordErrors.submit}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className={`
                        w-full py-2 px-4 rounded-lg font-semibold text-white
                        transition duration-200
                        ${loading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                        }
                      `}
                    >
                      {loading ? 'Updating...' : 'Change Password'}
                    </button>
                  </form>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    Notification Preferences
                  </h2>

                  <div className="space-y-4 max-w-md mb-6">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-800">WhatsApp Notifications</p>
                        <p className="text-sm text-gray-600">
                          Receive updates via WhatsApp
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.whatsapp}
                          onChange={() => handleNotificationChange('whatsapp')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-800">Email Notifications</p>
                        <p className="text-sm text-gray-600">
                          Receive updates via email
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.email}
                          onChange={() => handleNotificationChange('email')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-800">SMS Notifications</p>
                        <p className="text-sm text-gray-600">
                          Receive updates via SMS
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.sms}
                          onChange={() => handleNotificationChange('sms')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={handleSaveNotifications}
                    disabled={loading}
                    className={`
                      px-6 py-2 rounded-lg font-semibold text-white
                      transition duration-200
                      ${loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                      }
                    `}
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              )}

              {/* Bank Details Tab */}
              {activeTab === 'bank' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    Bank Details
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Your bank details have been verified. To update them, please contact support.
                  </p>
                  <a
                    href="/seller/support"
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    Contact Support
                  </a>
                </div>
              )}

              {/* Tax Details Tab */}
              {activeTab === 'tax' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    Tax Details
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Your tax information is securely stored. For changes, please reach out to our support team.
                  </p>
                  <a
                    href="/seller/support"
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    Contact Support
                  </a>
                </div>
              )}

              {/* E-Signature Tab */}
              {activeTab === 'signature' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    E-Signature Upload
                  </h2>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                      Upload your digital signature for account verification.
                    </p>
                  </div>
                  <button className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition">
                    Upload Signature
                  </button>
                </div>
              )}

              {/* Legal Tab */}
              {activeTab === 'legal' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    Legal & Policies
                  </h2>
                  <div className="space-y-4">
                    <a href="/terms" className="block text-blue-600 hover:underline font-semibold">
                      Terms & Conditions
                    </a>
                    <a href="/privacy" className="block text-blue-600 hover:underline font-semibold">
                      Privacy Policy
                    </a>
                    <a href="/supplier-agreement" className="block text-blue-600 hover:underline font-semibold">
                      Supplier Agreement
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerSettings;
